import type {
  LaSickClaimType, LaSickExportSuffix, LaSickJob, LaSickPolicy, LaSickResponse, LaSickRow, LaSickStatus, LaSickWeekRef,
} from '../../api/laSick';

/**
 * Pure helpers for the v2 LA Sick page (NPM payroll). No React, no DOM.
 *
 * Rules from docs/featureBriefs/design_handoff_la_sick_page_design/README.md, with the
 * data facts that differ from the design:
 *  - NPM pay periods run Thursday – Wednesday. Week labels always use the API's weekNumber +
 *    startDate (the period start); a Monday is never computed. A week whose AccountingYear
 *    differs from the selected year (NPM W1 of 2026 = Dec 25 – 31, 2025) carries the year so it
 *    can't be mistaken for week #1 of the selected year.
 *  - Week-range filters compare the period START DATE (not the week number), because the week
 *    numbers of a calendar year wrap (… #52, #1).
 *  - Status order Needs → Has → No work → Needed (inactive); Needed (inactive) rows show only
 *    under All. Past years have no status and no policy.
 *  - Default sort Status, then Last LA week newest, then Employee; every sort breaks ties that way.
 *  - Dates are YYYY-MM-DD strings, only ever turned into Dates via Date.UTC.
 */

/* ------------------------------------------------------------------ format */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The first year with NPM data (Apr 2025); the year stepper stops here. */
export const LA_SICK_FIRST_YEAR = 2025;

export function plural(n: number, word: string, pluralWord?: string): string {
  return `${n} ${n === 1 ? word : pluralWord ?? `${word}s`}`;
}

/** $1,240.00 */
export function money(n: number): string {
  return '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Hours: one decimal minimum, two at most — 16.0, 7.75, 1,240.5. */
/** The current year in the tenant zone (Pacific), matching the API's "current year". */
export function pacificYear(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric' }).format(now));
}

export function hrs(n: number): string {
  return (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function utc(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

const yy = (y: number) => `’${String(y).slice(-2)}`;

/** "Oct 8"; adds ’25 when the date's year isn't `year`. */
export function mdShort(date: string, year?: number): string {
  if (!date) return '';
  const t = utc(date);
  const base = `${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()}`;
  return year != null && t.getUTCFullYear() !== year ? `${base} ${yy(t.getUTCFullYear())}` : base;
}

/** "Oct 8 – 14", "Sep 29 – Oct 5"; `withYear` appends ", 2026" (or spans "Dec 25, 2025 – Dec 31, 2025"). */
export function fmtRange(start: string, end: string, withYear = false): string {
  if (!start || !end) return '';
  const a = utc(start);
  const b = utc(end);
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
  const left = `${MONTHS[a.getUTCMonth()]} ${a.getUTCDate()}${withYear && !sameYear ? `, ${a.getUTCFullYear()}` : ''}`;
  const right = a.getUTCMonth() === b.getUTCMonth() && sameYear
    ? `${b.getUTCDate()}`
    : `${MONTHS[b.getUTCMonth()]} ${b.getUTCDate()}`;
  return `${left} – ${right}${withYear ? `, ${b.getUTCFullYear()}` : ''}`;
}

/** Week badge text: "#42", or "#1 ’26" when the week belongs to another accounting year. */
export function weekBadge(w: Pick<LaSickWeekRef, 'weekNumber' | 'accountingYear'>, year: number): string {
  return w.accountingYear && w.accountingYear !== year ? `#${w.weekNumber} ${yy(w.accountingYear)}` : `#${w.weekNumber}`;
}

/** "week #1 of 2026 (Dec 25 – 31, 2025)" — for titles. */
export function weekTitle(w: LaSickWeekRef): string {
  return `Week #${w.weekNumber} of ${w.accountingYear} (${fmtRange(w.startDate, w.endDate, true)})`;
}

/** "Oct 21, 2:00 AM" in the viewer's zone (or `timeZone`); '' when missing / invalid. */
export function fmtSyncTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}),
  }).replace(/[  ]/g, ' '); // newer ICU puts a narrow no-break space before AM/PM
}

/** "Aug 1, 2026" */
export function fmtDate(date: string | null | undefined): string {
  if (!date) return '';
  const t = utc(date);
  return `${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()}, ${t.getUTCFullYear()}`;
}

/** Years for the stepper: current back to LA_SICK_FIRST_YEAR (newest first). */
export function yearOptions(currentYear: number): number[] {
  const out: number[] = [];
  for (let y = currentYear; y >= LA_SICK_FIRST_YEAR; y--) out.push(y);
  return out.length ? out : [currentYear];
}

/* ------------------------------------------------------------------ rows */

export type StKey = 'needs' | 'has' | 'nowork' | 'inact';

export const ST_LABEL: Record<StKey, string> = {
  needs: 'Needs LA Sick',
  has: 'Has LA Sick',
  nowork: 'LA Sick, no LA work',
  inact: 'Needed (inactive)',
};

/** Status sort order and the Status filter's option order. */
export const ST_ORDER: StKey[] = ['needs', 'has', 'nowork', 'inact'];

const ST_FROM_API: Record<LaSickStatus, StKey> = {
  NEEDS_LA_SICK: 'needs',
  HAS_LA_SICK: 'has',
  LA_SICK_NO_WORK: 'nowork',
  NEEDED_INACTIVE: 'inact',
};

/** Policy filter value for rows with no open time-off policy. */
export const NO_POLICY = 'No policy';

export interface LaRow {
  src: LaSickRow;
  id: number;
  /** "First Last" */
  name: string;
  /** Sort key: "last first". */
  sortName: string;
  /** Business employee ID (altEmployeeID), as shown on screen. */
  empID: string;
  active: boolean;
  terminatedOn: string | null;
  st: StKey | null;
  isNew: boolean;
  hrs: number;
  pc: number;
  wks: number;
  first: LaSickWeekRef | null;
  last: LaSickWeekRef | null;
  jobs: LaSickJob[];
  policy: LaSickPolicy | null;
  /** Policy name, or NO_POLICY. */
  policyName: string;
  /** Lines in the export's Weekly detail sheet (sum of weeks[].lines). */
  detailLines: number;
}

export function deriveRow(r: LaSickRow): LaRow {
  const first = (r.firstName ?? '').trim();
  const last = (r.lastName ?? '').trim();
  return {
    src: r,
    id: r.employeeID,
    name: [first, last].filter(Boolean).join(' '),
    sortName: [last, first].filter(Boolean).join(' '),
    empID: String(r.altEmployeeID || r.employeeID || ''),
    active: !!r.active,
    terminatedOn: r.terminatedOn ?? null,
    st: r.status ? ST_FROM_API[r.status] ?? null : null,
    isNew: !!r.isNew,
    hrs: Number(r.laHours) || 0,
    pc: Number(r.laPieceAmount) || 0,
    wks: Number(r.laWeekCount) || 0,
    first: r.firstWeek ?? null,
    last: r.lastWeek ?? null,
    jobs: r.jobs ?? [],
    policy: r.policy ?? null,
    policyName: r.policy?.name?.trim() || NO_POLICY,
    detailLines: (r.weeks ?? []).reduce((s, w) => s + (w.lines?.length ?? 0), 0),
  };
}

/** Active employees, plus inactive ones when Show inactive is on. */
export function population(rows: LaRow[], showInactive: boolean): LaRow[] {
  return showInactive ? rows : rows.filter((r) => r.active);
}

/* ------------------------------------------------------------------ scope (quick filter) */

export type Scope = 'needs' | 'has' | 'nowork' | 'all';
export const SCOPES: { key: Scope; label: string }[] = [
  { key: 'needs', label: 'Needs LA Sick' },
  { key: 'has', label: 'Has LA Sick' },
  { key: 'nowork', label: 'LA Sick, no LA work' },
  { key: 'all', label: 'All' },
];

/** Past years have no status, so everything is in scope. Needed (inactive) is only under All. */
export function inScope(row: Pick<LaRow, 'st'>, scope: Scope, isCurrentYear = true): boolean {
  if (!isCurrentYear || scope === 'all') return true;
  return row.st === scope;
}

export function scopeCounts(rows: Pick<LaRow, 'st'>[]): Record<Scope, number> {
  const c: Record<Scope, number> = { needs: 0, has: 0, nowork: 0, all: rows.length };
  for (const r of rows) {
    if (r.st === 'needs') c.needs++;
    else if (r.st === 'has') c.has++;
    else if (r.st === 'nowork') c.nowork++;
  }
  return c;
}

export function exportSuffix(scope: Scope, isCurrentYear: boolean): LaSickExportSuffix | undefined {
  if (!isCurrentYear) return undefined;
  if (scope === 'needs') return 'NeedsLASick';
  if (scope === 'has') return 'HasLASick';
  if (scope === 'nowork') return 'LASickNoLAWork';
  return undefined;
}

/** Same as the API's name — used only when the response has no Content-Disposition. */
export function exportFileName(
  data: Pick<LaSickResponse, 'year' | 'isCurrentYear' | 'throughWeek'>,
  scope: Scope,
): string {
  if (!data.isCurrentYear) return `LASick_${data.year}_LAwork.xlsx`;
  const thru = data.throughWeek ? `_thru-wk${data.throughWeek.weekNumber}` : '';
  const sfx = exportSuffix(scope, true);
  return `LASick_${data.year}${thru}${sfx ? `_${sfx}` : ''}.xlsx`;
}

/** The export's Weekly detail line count for these rows. */
export function detailLineCount(rows: Pick<LaRow, 'detailLines'>[]): number {
  return rows.reduce((s, r) => s + r.detailLines, 0);
}

/* ------------------------------------------------------------------ filters */

export interface ColumnFilters {
  emp: string;
  empId: string;
  st: StKey[];
  /** projectIDs */
  jobs: number[];
  /** Policy names (or NO_POLICY). */
  pol: string[];
  /** Week START dates (YYYY-MM-DD), '' = open. */
  firstFrom: string;
  firstTo: string;
  lastFrom: string;
  lastTo: string;
}

export type FilterCol = 'emp' | 'st' | 'first' | 'last' | 'jobs' | 'pol';
export type ChipKey = FilterCol | 'empId';

export const emptyFilters = (): ColumnFilters => ({
  emp: '', empId: '', st: [], jobs: [], pol: [], firstFrom: '', firstTo: '', lastFrom: '', lastTo: '',
});

/** Week numbers differ between years, so a year change clears the week-range filters only. */
export function clearWeekFilters(f: ColumnFilters): ColumnFilters {
  return { ...f, firstFrom: '', firstTo: '', lastFrom: '', lastTo: '' };
}

const low = (s: string | null | undefined) => String(s ?? '').toLowerCase();
const stripHash = (s: string) => s.trim().replace(/^#/, '').trim();

/** Name, employee ID (a leading # is ignored) or job number. */
export function matchesSearch(row: LaRow, q: string): boolean {
  const t = stripHash(q).toLowerCase();
  if (!t) return true;
  return low(row.name).includes(t)
    || low(row.sortName).includes(t)
    || low(row.empID).includes(t)
    || row.jobs.some((j) => low(j.altProjectID).includes(t));
}

/** Inclusive range on a week start date; rows with no LA work never match a set range. */
function inWeekRange(w: LaSickWeekRef | null, from: string, to: string): boolean {
  if (!from && !to) return true;
  if (!w || !w.startDate) return false;
  return (!from || w.startDate >= from) && (!to || w.startDate <= to);
}

/** Status and Policy filters only apply to the current year (past years have neither). */
export function matchesFilters(row: LaRow, f: ColumnFilters, isCurrentYear = true): boolean {
  const emp = f.emp.trim().toLowerCase();
  if (emp && !low(row.name).includes(emp) && !low(row.sortName).includes(emp)) return false;
  const empId = stripHash(f.empId).toLowerCase();
  if (empId && !low(row.empID).includes(empId)) return false;
  if (isCurrentYear && f.st.length && !(row.st && f.st.includes(row.st))) return false;
  if (f.jobs.length && !row.jobs.some((j) => f.jobs.includes(j.projectID))) return false;
  if (isCurrentYear && f.pol.length && !f.pol.includes(row.policyName)) return false;
  if (!inWeekRange(row.first, f.firstFrom, f.firstTo)) return false;
  if (!inWeekRange(row.last, f.lastFrom, f.lastTo)) return false;
  return true;
}

export function isColumnFiltered(f: ColumnFilters, col: FilterCol): boolean {
  switch (col) {
    case 'emp': return !!(f.emp.trim() || f.empId.trim());
    case 'st': return f.st.length > 0;
    case 'jobs': return f.jobs.length > 0;
    case 'pol': return f.pol.length > 0;
    case 'first': return !!(f.firstFrom || f.firstTo);
    case 'last': return !!(f.lastFrom || f.lastTo);
  }
}

export function clearFilter(f: ColumnFilters, key: ChipKey): ColumnFilters {
  switch (key) {
    case 'emp': return { ...f, emp: '', empId: '' };
    case 'empId': return { ...f, empId: '' };
    case 'st': return { ...f, st: [] };
    case 'jobs': return { ...f, jobs: [] };
    case 'pol': return { ...f, pol: [] };
    case 'first': return { ...f, firstFrom: '', firstTo: '' };
    case 'last': return { ...f, lastFrom: '', lastTo: '' };
  }
}

export interface Chip { key: ChipKey; label: string; value: string; title?: string }

/**
 * Chips for the active column filters. `weekLabel` turns a week start date into its label
 * (e.g. "#30"); `jobLabel` a projectID into its job number. Status / Policy chips are hidden
 * for past years (the filters are kept and restored on returning to the current year).
 */
export function filterChips(
  f: ColumnFilters,
  isCurrentYear: boolean,
  weekLabel: (startDate: string) => string,
  jobLabel: (projectID: number) => string,
): Chip[] {
  const out: Chip[] = [];
  const q = (s: string) => `“${s.trim()}”`;
  const first = (a: string[]) => a[0] + (a.length > 1 ? ` +${a.length - 1}` : '');
  const rng = (a: string, b: string) => (a && b ? `${weekLabel(a)} – ${weekLabel(b)}` : a ? `≥ ${weekLabel(a)}` : `≤ ${weekLabel(b)}`);
  if (f.emp.trim()) out.push({ key: 'emp', label: 'Employee', value: q(f.emp) });
  if (f.empId.trim()) out.push({ key: 'empId', label: 'Employee ID', value: q(f.empId) });
  if (isCurrentYear && f.st.length) {
    const ls = ST_ORDER.filter((s) => f.st.includes(s)).map((s) => ST_LABEL[s]);
    out.push({ key: 'st', label: 'Status', value: first(ls), title: ls.join(', ') });
  }
  if (f.firstFrom || f.firstTo) out.push({ key: 'first', label: 'First LA week', value: rng(f.firstFrom, f.firstTo) });
  if (f.lastFrom || f.lastTo) out.push({ key: 'last', label: 'Last LA week', value: rng(f.lastFrom, f.lastTo) });
  if (f.jobs.length) {
    const ls = f.jobs.map(jobLabel);
    out.push({ key: 'jobs', label: 'LA jobs', value: first(ls), title: ls.join(', ') });
  }
  if (isCurrentYear && f.pol.length) out.push({ key: 'pol', label: 'Policy', value: first(f.pol), title: f.pol.join(', ') });
  return out;
}

/* ------------------------------------------------------------------ popover options + counts */

/** Distinct LA jobs across rows, by job number. */
export function jobOptions(rows: LaRow[]): LaSickJob[] {
  const m = new Map<number, LaSickJob>();
  rows.forEach((r) => r.jobs.forEach((j) => { if (!m.has(j.projectID)) m.set(j.projectID, j); }));
  return Array.from(m.values()).sort((a, b) => a.altProjectID.localeCompare(b.altProjectID, undefined, { numeric: true }) || a.projectID - b.projectID);
}

/** Distinct weeks with LA work across rows, oldest first. */
export function weekOptions(rows: LaRow[]): LaSickWeekRef[] {
  const m = new Map<number, LaSickWeekRef>();
  rows.forEach((r) => (r.src.weeks ?? []).forEach((w) => {
    if (w.startDate && !m.has(w.payPeriodID)) {
      const { lines, ...ref } = w;
      m.set(w.payPeriodID, ref);
    }
  }));
  return Array.from(m.values()).sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));
}

/** Policy names present (A–Z), then NO_POLICY. */
export function policyOptions(rows: LaRow[]): string[] {
  const names = Array.from(new Set(rows.map((r) => r.policyName).filter((n) => n !== NO_POLICY)));
  names.sort((a, b) => a.localeCompare(b));
  names.push(NO_POLICY);
  return names;
}

export function columnCounts(rows: LaRow[]): {
  st: Record<string, number>;
  jobs: Record<number, number>;
  pol: Record<string, number>;
} {
  const st: Record<string, number> = {};
  const jobs: Record<number, number> = {};
  const pol: Record<string, number> = {};
  for (const r of rows) {
    if (r.st) st[r.st] = (st[r.st] ?? 0) + 1;
    pol[r.policyName] = (pol[r.policyName] ?? 0) + 1;
    new Set(r.jobs.map((j) => j.projectID)).forEach((id) => { jobs[id] = (jobs[id] ?? 0) + 1; });
  }
  return { st, jobs, pol };
}

/* ------------------------------------------------------------------ sort */

export type SortKey = 'emp' | 'st' | 'hrs' | 'pc' | 'wks' | 'first' | 'last' | 'jobs' | 'pol';
export interface SortState { key: SortKey; dir: 'asc' | 'desc' }

export const DEFAULT_SORT: SortState = { key: 'st', dir: 'asc' };
export const PAST_DEFAULT_SORT: SortState = { key: 'last', dir: 'desc' };

export const SORT_LABEL: Record<SortKey, string> = {
  emp: 'Employee', st: 'Status', hrs: 'LA hours', pc: 'LA piece $', wks: 'LA weeks',
  first: 'First LA week', last: 'Last LA week', jobs: 'LA jobs', pol: 'Time-off policy',
};

const DESC_FIRST: SortKey[] = ['hrs', 'pc', 'wks', 'last', 'jobs'];

export function nextSort(cur: SortState, key: SortKey): SortState {
  if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: DESC_FIRST.includes(key) ? 'desc' : 'asc' };
}

/** Past years have no Status / Policy columns: those sorts fall back to Last LA week newest. */
export function effectiveSort(sort: SortState, isCurrentYear: boolean): SortState {
  if (!isCurrentYear && (sort.key === 'st' || sort.key === 'pol')) return PAST_DEFAULT_SORT;
  return sort;
}

const cmpText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
const stIdx = (r: LaRow) => (r.st ? ST_ORDER.indexOf(r.st) : 0);
const cmpStr = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Status order, then Last LA week newest (no work last), then Employee, then id. */
function tie(a: LaRow, b: LaRow): number {
  return stIdx(a) - stIdx(b)
    || cmpStr(b.last?.startDate ?? '', a.last?.startDate ?? '')
    || cmpText(a.sortName, b.sortName)
    || a.id - b.id;
}

/** Sort a copy. */
export function sortRows(rows: LaRow[], sort: SortState, isCurrentYear = true): LaRow[] {
  const s = effectiveSort(sort, isCurrentYear);
  const d = s.dir === 'asc' ? 1 : -1;
  const primary = (a: LaRow, b: LaRow): number => {
    switch (s.key) {
      case 'emp': return cmpText(a.sortName, b.sortName) * d;
      case 'st': return (stIdx(a) - stIdx(b)) * d;
      case 'hrs': return (a.hrs - b.hrs) * d;
      case 'pc': return (a.pc - b.pc) * d;
      case 'wks': return (a.wks - b.wks) * d;
      // No LA work sorts after any week ascending, before any week descending.
      case 'first': return cmpStr(a.first?.startDate ?? '9999', b.first?.startDate ?? '9999') * d;
      case 'last': return cmpStr(a.last?.startDate ?? '', b.last?.startDate ?? '') * d;
      case 'jobs': return (a.jobs.length - b.jobs.length) * d;
      case 'pol': return cmpText(a.policyName, b.policyName) * d;
    }
  };
  return rows.slice().sort((a, b) => primary(a, b) || tie(a, b));
}

/* ------------------------------------------------------------------ summary strip */

export interface YearSummary {
  withWork: number;
  needs: number;
  has: number;
  nowork: number;
  /** Inactive employees with LA work and no LA Sick (never counted in needs). */
  neededInactive: number;
  laHours: number;
}

/**
 * Year totals for the population (D6) — they do NOT follow the quick filter or search. With
 * Show inactive off this is the API's active-only totals; with it on, it is recomputed from
 * every row (active + inactive).
 */
export function yearSummary(
  data: Pick<LaSickResponse, 'totals'>,
  rows: LaRow[],
  showInactive: boolean,
): YearSummary {
  if (!showInactive) {
    const t = data.totals;
    return {
      withWork: t.employeesWithLaWork ?? 0,
      needs: t.needs ?? 0,
      has: t.has ?? 0,
      nowork: t.noWork ?? 0,
      neededInactive: 0,
      laHours: t.laHours ?? 0,
    };
  }
  return summarizeRows(rows);
}

/** The same totals computed from rows (whatever population they are). */
export function summarizeRows(rows: LaRow[]): YearSummary {
  const s: YearSummary = { withWork: 0, needs: 0, has: 0, nowork: 0, neededInactive: 0, laHours: 0 };
  let hours = 0;
  for (const r of rows) {
    if (r.wks > 0) s.withWork++;
    if (r.st === 'needs') s.needs++;
    else if (r.st === 'has') s.has++;
    else if (r.st === 'nowork') s.nowork++;
    else if (r.st === 'inact') s.neededInactive++;
    hours += r.hrs;
  }
  s.laHours = Math.round(hours * 100) / 100;
  return s;
}

/* ------------------------------------------------------------------ job chip fitting */

/**
 * Job chips sit on ONE line: at most `max` chips, then `+n` for the rest. Given each chip's
 * natural width, the `+n` chip's width, the gap and the available width, return how many
 * leading chips to show (at least 1 — the first truncates if even it doesn't fit). Runs on
 * render and resize only.
 */
export function fitJobChips(widths: number[], moreWidth: number, gap: number, avail: number, max = 2): number {
  const n = widths.length;
  if (n === 0) return 0;
  for (let shown = Math.min(n, max); shown >= 1; shown--) {
    let w = 0;
    for (let i = 0; i < shown; i++) w += widths[i] + (i ? gap : 0);
    if (shown < n) w += gap + moreWidth;
    if (w <= avail + 1) return shown;
  }
  return 1;
}

/* ------------------------------------------------------------------ detail panel */

export interface JobLine { job: LaSickJob | null; projectID: number; claimType: LaSickClaimType; hours: number; pieceAmount: number }

/** The week groups for the panel, newest first, with each line's job resolved. */
export function weeksNewestFirst(row: LaRow): { week: LaSickWeekRef; hours: number; piece: number; lines: JobLine[] }[] {
  const jobs = new Map(row.jobs.map((j) => [j.projectID, j]));
  return (row.src.weeks ?? [])
    .slice()
    .sort((a, b) => cmpStr(b.startDate, a.startDate) || b.payPeriodID - a.payPeriodID)
    .map((w) => {
      const { lines, ...week } = w;
      return {
        week,
        hours: lines.reduce((s, l) => s + (Number(l.hours) || 0), 0),
        piece: lines.reduce((s, l) => s + (Number(l.pieceAmount) || 0), 0),
        lines: lines.map((l) => ({ ...l, job: jobs.get(l.projectID) ?? null })),
      };
    });
}
